// Define a type, revise a type (02-DESIGN/03-console/02-forms-from-schemas.md
// § where it applies: a store type; 02-DESIGN/10-state/01-stores-types-and-
// instances.md § the console: Stores; 01-the-console.md § conventions: a
// form): a form in the Settings tab's place to define one, and in the type's
// page's place to revise it, drawing the record as a
// form — what an item holds, its history, what it may hold, its changes —
// with the service's refusal shown beside it, and Edit as JSON in its head
// for the whole record. A record the form cannot draw opens on JSON with the
// reason. The record saved is the record the service already takes.

import { Braces, ListTree, Plus, X } from "lucide-react";
import { useState } from "react";

import { isRefusal } from "../../platform/bus.ts";
import {
  Button,
  Choices,
  Field,
  NotAllowed,
  Notice,
  Select,
  TextArea,
  TextInput,
} from "../../ui/components.tsx";
import { FormPage } from "../../ui/form.tsx";
import { ShowMore } from "../../ui/more.tsx";
import { ShapeEditor } from "../../ui/schema/SchemaForms.tsx";
import { newId, type Problem } from "../../ui/schema/shape.ts";
import {
  Code,
  Effect,
  HistoryPolicy,
  parseTypeRecord,
  typeRecordText,
  validName,
  type NamedType,
  type TypeRecord,
} from "../../wire/state.ts";
import { useSurface } from "../surface.tsx";
import {
  EFFECT_WORDS,
  HISTORY_WORDS,
  blankChange,
  blankType,
  fieldsLine,
  typeDraftOf,
  typeProblems,
  typeRecordOf,
  type ChangeRow,
  type ChildRow,
  type Data,
  type TypeDraft,
} from "./typeForm.ts";

type Mode = "form" | "json";

function said(problems: readonly Problem[], id: string) {
  return problems
    .filter((p) => p.id === id)
    .map((p) => (
      <p key={p.what} className="small bad">
        {p.what}
      </p>
    ));
}

export function DefineType({
  store,
  existing,
  types,
  heading,
  back,
  onClose,
  onDefined,
}: {
  store: string;
  existing: NamedType | undefined;
  /** h2 in the Settings tab's place, where a type is defined; h1 in the type's page's place, where it is revised. */
  heading?: "h1" | "h2";
  /** What the form stands in front of, drawn as its crumb. */
  back: string;
  /** The store's types, for what an item may hold. */
  types: readonly NamedType[];
  onClose: () => void;
  onDefined: (name: string, revision: number | undefined) => void;
}) {
  const s = useSurface();
  const [start] = useState(() => {
    if (existing === undefined) {
      return { mode: "form" as Mode, draft: blankType(), text: "", why: undefined };
    }
    const d = typeDraftOf(existing);
    return "reason" in d
      ? { mode: "json" as Mode, draft: blankType(), text: typeRecordText(existing), why: d.reason }
      : { mode: "form" as Mode, draft: d, text: "", why: undefined };
  });
  const [mode, setMode] = useState<Mode>(start.mode);
  const [draft, setDraft] = useState<TypeDraft>(start.draft);
  const [text, setText] = useState(start.text);
  const [why, setWhy] = useState<string | undefined>(start.why);
  const [name, setName] = useState(existing?.name ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const [conflict, setConflict] = useState(false);

  const record: TypeRecord | Error = mode === "form" ? typeRecordOf(draft) : parseTypeRecord(text);
  const problems = mode === "form" ? typeProblems(draft) : [];
  const nameOK = validName(name.trim());
  const ready = nameOK && !(record instanceof Error) && problems.length === 0;

  const toJSON = () => {
    setText(typeRecordText(typeRecordOf(draft)));
    setWhy(undefined);
    setMode("json");
  };
  const toForm = () => {
    const parsed = parseTypeRecord(text);
    if (parsed instanceof Error) {
      setWhy(`The record is not readable yet: ${parsed.message}.`);
      return;
    }
    const d = typeDraftOf(parsed);
    if ("reason" in d) {
      setWhy(d.reason);
      return;
    }
    setDraft(d);
    setWhy(undefined);
    setMode("form");
  };

  const submit = () => {
    if (busy || !ready || record instanceof Error) {
      return;
    }
    setErr(undefined);
    setConflict(false);
    setBusy(true);
    s.bus
      .stores()
      .defineType(store, name.trim(), record, existing?.revision)
      .then(
        (rec) => {
          setBusy(false);
          onDefined(name.trim(), rec.revision);
        },
        (e: unknown) => {
          setBusy(false);
          if (isRefusal(e, Code.conflict)) {
            setConflict(true);
          } else {
            setErr(e);
          }
        },
      );
  };

  const typeNames = [...new Set([...types.map((t) => t.name), ...(nameOK ? [name.trim()] : [])])].sort();

  return (
    <FormPage
      title={existing === undefined ? "Define a type" : `Revise ${existing.name}`}
      heading={heading}
      back={back}
      onClose={onClose}
      actions={
        mode === "form" ? (
          <Button size="sm" kind="quiet" onClick={toJSON}>
            <Braces strokeWidth={1.8} aria-hidden="true" />
            Edit as JSON
          </Button>
        ) : (
          <Button size="sm" kind="quiet" onClick={toForm}>
            <ListTree strokeWidth={1.8} aria-hidden="true" />
            Edit as a form
          </Button>
        )
      }
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || !ready}>
            {busy ? "Saving…" : existing === undefined ? "Define" : "Save"}
          </Button>
        </>
      }
    >
      <p className="muted">
        A type says what an item holds, whether its history may be compacted, what may live under it, and the
        changes it admits. Adding is safe; changing what a change does folds the store&apos;s state again.
      </p>
      {err !== undefined && <NotAllowed err={err} what="Define" />}
      {conflict && (
        <Notice kind="bad" title="Someone saved a newer version">
          Cancel and revise it again to see it.
        </Notice>
      )}
      {why !== undefined && <Notice title="Shown as JSON">{why}</Notice>}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {existing === undefined && (
          <Field
            label="Name"
            hint={
              nameOK || name.trim() === ""
                ? "Lowercase letters, digits and hyphens."
                : "Lowercase letters, digits and hyphens only."
            }
          >
            <TextInput value={name} onChange={setName} placeholder="invoice" mono required autoFocus />
          </Field>
        )}
        {mode === "json" ? (
          <Field
            label="Record"
            hint={
              record instanceof Error ? record.message : "schema, history, children, operations — as JSON."
            }
          >
            <TextArea value={text} onChange={setText} rows={18} mono autoFocus={existing !== undefined} />
          </Field>
        ) : (
          <TypeParts
            draft={draft}
            onChange={setDraft}
            problems={problems}
            typeNames={typeNames}
            counted={existing !== undefined}
          />
        )}
      </form>
    </FormPage>
  );
}

function TypeParts({
  draft,
  onChange,
  problems,
  typeNames,
  counted,
}: {
  draft: TypeDraft;
  onChange: (d: TypeDraft) => void;
  problems: readonly Problem[];
  typeNames: readonly string[];
  /** A type already defined shows its fields counted first, with Show more (01-the-console.md § conventions: density and length). */
  counted: boolean;
}) {
  const fields = draft.shape.fields;
  // A refusal naming a field opens the editor, so the problem is never hidden.
  const [more, setMore] = useState(!counted || problems.length > 0);
  const open = more || fields.length === 0 || problems.length > 0;
  return (
    <>
      <div className="part">
        <h3>What an item holds</h3>
        {fields.length === 0 && (
          <p className="small muted">No fields: an item of this type may hold anything.</p>
        )}
        {!open && (
          <p className="small">
            {fieldsLine(fields.map((f) => f.name))} <ShowMore open={false} onToggle={setMore} />
          </p>
        )}
        {open && (
          <ShapeEditor
            fields={fields}
            problems={problems}
            onChange={(next) => {
              onChange({ ...draft, shape: { ...draft.shape, fields: next } });
            }}
          />
        )}
      </div>
      <div className="part">
        <h3>History</h3>
        <Choices
          name="history"
          value={draft.history ?? HistoryPolicy.compactable}
          onChange={(history) => {
            onChange({ ...draft, history });
          }}
          options={[
            {
              value: HistoryPolicy.compactable,
              label: HISTORY_WORDS[HistoryPolicy.compactable],
              hint: "An item's earlier changes may be replaced by a snapshot of its current.",
            },
            {
              value: HistoryPolicy.full,
              label: HISTORY_WORDS[HistoryPolicy.full],
              hint: "Every change is kept; no snapshot replaces them.",
            },
          ]}
        />
      </div>
      <MayHold
        rows={draft.children}
        typeNames={typeNames}
        problems={problems}
        onChange={(children) => {
          onChange({ ...draft, children });
        }}
      />
      <Changes
        rows={draft.changes}
        problems={problems}
        onChange={(changes) => {
          onChange({ ...draft, changes });
        }}
      />
    </>
  );
}

function MayHold({
  rows,
  typeNames,
  problems,
  onChange,
}: {
  rows: ChildRow[];
  typeNames: readonly string[];
  problems: readonly Problem[];
  onChange: (rows: ChildRow[]) => void;
}) {
  const cols = { gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) 36px" };
  return (
    <div className="part">
      <h3>May hold</h3>
      {rows.length === 0 && <p className="small muted">Nothing lives under an item of this type.</p>}
      {rows.map((c, i) => {
        const names = [...new Set([...typeNames, ...(c.type === "" ? [] : [c.type])])];
        const set = (nc: ChildRow) => {
          onChange(rows.map((x, j) => (j === i ? nc : x)));
        };
        return (
          <div key={c.id}>
            <div className="part-rows" style={cols}>
              <input
                className="input mono"
                aria-label="Name"
                value={c.name}
                placeholder="comments"
                spellCheck={false}
                autoComplete="off"
                onChange={(e) => {
                  set({ ...c, name: e.target.value });
                }}
              />
              <Select
                value={c.type}
                onChange={(type) => {
                  set({ ...c, type });
                }}
                options={[
                  { value: "", label: "Choose a type" },
                  ...names.map((n) => ({ value: n, label: n })),
                ]}
              />
              <Button
                kind="icon"
                label={`Remove ${c.name || "this line"}`}
                onClick={() => {
                  onChange(rows.filter((_, j) => j !== i));
                }}
              >
                <X size={16} strokeWidth={1.6} />
              </Button>
            </div>
            {said(problems, c.id)}
          </div>
        );
      })}
      <div>
        <Button
          size="sm"
          kind="quiet"
          onClick={() => {
            onChange([...rows, { id: newId(), name: "", type: "" }]);
          }}
        >
          <Plus strokeWidth={1.8} aria-hidden="true" />
          Add
        </Button>
      </div>
    </div>
  );
}

function Changes({
  rows,
  problems,
  onChange,
}: {
  rows: ChangeRow[];
  problems: readonly Problem[];
  onChange: (rows: ChangeRow[]) => void;
}) {
  const cols = { gridTemplateColumns: "minmax(0, 1fr) 170px 170px 36px" };
  return (
    <div className="part">
      <h3>Changes</h3>
      {rows.length === 0 ? (
        <p className="small muted">No changes declared: an item of this type changes only by a snapshot.</p>
      ) : (
        <div className="part-rows shape-head" style={cols} aria-hidden="true">
          <span>Change</span>
          <span>What it does</span>
          <span>Its data</span>
          <span />
        </div>
      )}
      {rows.map((c, i) => {
        const set = (nc: ChangeRow) => {
          onChange(rows.map((x, j) => (j === i ? nc : x)));
        };
        return (
          <div key={c.id} className="shape-field">
            <div className="part-rows" style={cols}>
              <input
                className="input mono"
                aria-label="Change"
                value={c.name}
                placeholder="status.set"
                spellCheck={false}
                autoComplete="off"
                onChange={(e) => {
                  set({ ...c, name: e.target.value });
                }}
              />
              <Select
                value={c.effect ?? Effect.none}
                onChange={(effect) => {
                  set({ ...c, effect });
                }}
                options={[
                  { value: Effect.merge, label: EFFECT_WORDS[Effect.merge] },
                  { value: Effect.none, label: EFFECT_WORDS[Effect.none] },
                ]}
              />
              <Select<Data>
                value={c.data}
                onChange={(data) => {
                  set({ ...c, data });
                }}
                options={[
                  { value: "anything", label: "Anything" },
                  { value: "fields", label: "These fields" },
                ]}
              />
              <Button
                kind="icon"
                label={`Remove ${c.name || "this change"}`}
                onClick={() => {
                  onChange(rows.filter((_, j) => j !== i));
                }}
              >
                <X size={16} strokeWidth={1.6} />
              </Button>
            </div>
            {said(problems, c.id)}
            {c.data === "anything" && (c.effect ?? Effect.none) === Effect.merge && (
              <p className="small faint">The item&apos;s own shape still judges the result.</p>
            )}
            {c.data === "fields" && (
              <ShapeEditor
                fields={c.shape.fields}
                depth={1}
                problems={problems}
                onChange={(fields) => {
                  set({ ...c, shape: { ...c.shape, fields } });
                }}
              />
            )}
          </div>
        );
      })}
      <div>
        <Button
          size="sm"
          kind="quiet"
          onClick={() => {
            onChange([...rows, blankChange()]);
          }}
        >
          <Plus strokeWidth={1.8} aria-hidden="true" />
          Add a change
        </Button>
      </div>
    </div>
  );
}
